import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { FormattedMessage } from 'react-intl';

import Logger from '@mytaxi/logging';
import { Box, Headline, Link, Logo } from '@freenow/wave';

import { CenteredLoadingIndicator } from '../../common/components';
import { trackLoginError } from '../../tracking/helpers/googleTagManager/trackEvents';
import { AuthenticationContext, IStatePayload } from '../context/AuthenticationContext';
import { startAuthentication } from '../util/startAuthentication';

interface RedirectTarget extends IStatePayload {
    forwardedParameters: string;
}

export const OAuthCallback = () => {
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [redirectTarget, setRedirectTarget] = useState<RedirectTarget>();
    const { handleOAuthCallback } = useContext(AuthenticationContext);
    const location = useLocation();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        handleOAuthCallback(searchParams.get('code') || '', searchParams.get('state') || '')
            .then((payload: IStatePayload) => {
                setRedirectTarget({
                    ...payload,
                    forwardedParameters: extractParameters(payload.requestedParameters, searchParams),
                });
                setSuccess(true);
            })
            .catch((error) => {
                Logger.error('Failed to handle oauth callback', { error });
                setError(true);
            });
    }, [handleOAuthCallback, location.search]);

    const restartAuthentication = () =>
        startAuthentication(redirectTarget?.requestedUrl, { requestedParameters: redirectTarget?.requestedParameters });

    useEffect(() => {
        if (success) {
            const targetPath = redirectTarget?.requestedUrl || '/';
            const query = redirectTarget?.forwardedParameters || '';
            const fullUrl = query ? `${targetPath}?${query}` : targetPath;

            window.location.replace(fullUrl);
        }
    }, [success, redirectTarget]);

    if (error) {
        trackLoginError('Unknown');

        return (
            <Box p={6}>
                <Logo variant="business" monochrome width="180px" height="auto" />
                <Headline as="h3" mb={2} mt={5}>
                    <FormattedMessage id="login.errors.unknown" />
                </Headline>
                <Link onClick={restartAuthentication}>
                    <FormattedMessage id="login.tryAgain" />
                </Link>
            </Box>
        );
    }

    return success ? null : <CenteredLoadingIndicator />;
};

const extractParameters = (requestedParameters: string[] = [], searchParams: URLSearchParams): string => {
    const forwardedParameters = new URLSearchParams();

    for (const parameter of requestedParameters) {
        const value = searchParams.get(parameter);
        if (value !== null) {
            forwardedParameters.append(parameter, value);
        }
    }

    return forwardedParameters.toString();
};
